import { type Scale, type Severity } from './api-client/api-schemas'

/**
 * Returns a qualifier naming the units of a score scale, or undefined when the scale carries no
 * name worth showing. Callers append it to a group label, so an unnamed scale must yield undefined
 * rather than a generic fallback that would render as "Tool Severity (Severity)".
 *
 * Casing should be controlled by CSS (text-transform) rather than this function.
 */
export function getScaleQualifier(scale: Scale | undefined): string | undefined {
  return scale?.type === 'score' && scale.id === 'cvss' ? 'CVSS' : undefined
}

// `SeverityResponse.label` is optional in the spec because tools may publish numeric-only ranked sets;
// UI affordances require a human-readable label, so unlabeled severities are filtered out via this guard.
function hasSeverityLabel<S extends { label?: string }>(severity: S): severity is S & { label: string } {
  return severity.label !== undefined
}

/**
 * Returns a ranked-set scale's labelled severities, most severe first — the order every severity
 * control renders them in. Anything that is not a ranked set has no severities to rank.
 */
export function getSeveritiesByRankDescending(scale: Scale | undefined): (Severity & { label: string })[] {
  if (scale?.type !== 'ranked-set') {
    return []
  }

  return [...scale.severities].filter(hasSeverityLabel).sort((a, b) => b.rank - a.rank)
}
